import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { Robot } from '../labyrinth/Robot';

const TRACK_SAMPLE_DIST = 0.08;
const TRACK_MAX_AGE     = 45;
const TRACK_MAX_POINTS  = 2000;
const TRACK_LATERAL     = 0.33;
const TRACK_WIDTH       = 4.5;
// Built via THREE.Color so the sRGB hex gets converted to linear once. The
// per-vertex floats below need to be in linear space — the renderer re-encodes
// to sRGB on output, so writing a raw 0x11/255 = 0.067 float would land back
// as sRGB ~0.30 (mid-gray) and tracks look light instead of near-black.
const TRACK_COLOR_LINEAR = new THREE.Color(0x101010);
const Y_OFFSET          = 0.12;

// Each pair of consecutive points emits two thin quads (left + right track).
// One pair = 2 quads × 4 verts = 8 verts. Max pairs = MAX_POINTS - 1.
const MAX_PAIRS    = TRACK_MAX_POINTS - 1;
const MAX_SEGMENTS = MAX_PAIRS * 2;
const MAX_VERTS    = MAX_SEGMENTS * 4;
const MAX_INDICES  = MAX_SEGMENTS * 6;

interface TrackPoint {
  x: number;
  y: number;
  angle: number;
  age: number;
}

/**
 * Ground-plane tire tracks mirroring LabyrinthState.updateAndDrawTireTracks:
 * ring-buffered sample points, per-segment fade with age, viewport-culled
 * rendering into a single BufferGeometry rebuilt each frame.
 */
export class TireTracks3D {
  readonly group = new THREE.Group();
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly mesh: THREE.Mesh;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly indices: Uint32Array;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly colAttr: THREE.BufferAttribute;

  private readonly pool: TrackPoint[] = [];
  private head = 0;
  private count = 0;
  private lastX = -999;
  private lastY = -999;

  constructor() {
    this.positions = new Float32Array(MAX_VERTS * 3);
    this.colors    = new Float32Array(MAX_VERTS * 4);
    this.indices   = new Uint32Array(MAX_INDICES);
    for (let s = 0; s < MAX_SEGMENTS; s++) {
      const i = s * 4;
      const o = s * 6;
      this.indices[o]     = i;
      this.indices[o + 1] = i + 1;
      this.indices[o + 2] = i + 2;
      this.indices[o + 3] = i;
      this.indices[o + 4] = i + 2;
      this.indices[o + 5] = i + 3;
    }

    this.geometry = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(this.positions, 3);
    this.colAttr = new THREE.BufferAttribute(this.colors, 4);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.colAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('position', this.posAttr);
    this.geometry.setAttribute('color', this.colAttr);
    this.geometry.setIndex(new THREE.BufferAttribute(this.indices, 1));
    this.geometry.setDrawRange(0, 0);

    this.material = new THREE.MeshBasicMaterial({
      transparent: true,
      vertexColors: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      // Skip tone mapping so the dark track color stays dark — otherwise the
      // renderer's ACES + exposure 1.35 lifts tracks and floor in tandem and
      // the contrast collapses to nothing.
      toneMapped: false,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.renderOrder = 2;
    this.mesh.position.y = Y_OFFSET;
    // Geometry is rebuilt every frame with new positions, but Three.js caches
    // the first bounding sphere it computes — which is centered at the origin
    // (positions start as zeros). Once the camera leaves world origin the mesh
    // would be frustum-culled forever. We do our own viewport culling in update().
    this.mesh.frustumCulled = false;
    this.group.add(this.mesh);
  }

  /**
   * Advance the ring buffer and rebuild geometry for the current frame.
   * `viewTiles` is the camera's visible half-extent in tile units for culling.
   */
  update(dt: number, robot: Robot, camWorldX: number, camWorldY: number, viewTiles: number): void {
    const rx = robot.displayX;
    const ry = robot.displayY;
    const angle = robot.angle;

    // Sample new point if moved far enough.
    const ddx = rx - this.lastX;
    const ddy = ry - this.lastY;
    if (ddx * ddx + ddy * ddy >= TRACK_SAMPLE_DIST * TRACK_SAMPLE_DIST) {
      if (this.count < TRACK_MAX_POINTS) {
        if (this.pool.length <= this.head) {
          this.pool.push({ x: rx, y: ry, angle, age: 0 });
        } else {
          const pt = this.pool[this.head]!;
          pt.x = rx; pt.y = ry; pt.angle = angle; pt.age = 0;
        }
        this.count++;
      } else {
        const pt = this.pool[this.head]!;
        pt.x = rx; pt.y = ry; pt.angle = angle; pt.age = 0;
      }
      this.head = (this.head + 1) % TRACK_MAX_POINTS;
      this.lastX = rx;
      this.lastY = ry;
    }

    // Age all active points & expire the oldest prefix.
    let tail = (this.head - this.count + TRACK_MAX_POINTS * 2) % TRACK_MAX_POINTS;
    let expired = 0;
    for (let n = 0; n < this.count; n++) {
      const idx = (tail + n) % TRACK_MAX_POINTS;
      const pt = this.pool[idx]!;
      pt.age += dt;
      if (pt.age >= TRACK_MAX_AGE) expired = n + 1;
    }
    if (expired > 0) this.count -= expired;
    tail = (this.head - this.count + TRACK_MAX_POINTS * 2) % TRACK_MAX_POINTS;

    // Rebuild geometry — viewport-culled.
    const activeCount = this.count;
    if (activeCount < 2) {
      this.geometry.setDrawRange(0, 0);
      return;
    }

    const viewMinX = camWorldX - viewTiles;
    const viewMaxX = camWorldX + viewTiles;
    const viewMinY = camWorldY - viewTiles;
    const viewMaxY = camWorldY + viewTiles;

    const halfW = TRACK_WIDTH / 2;
    let segIdx = 0;

    for (let n = 1; n < activeCount; n++) {
      const p0 = this.pool[(tail + n - 1) % TRACK_MAX_POINTS]!;
      const p1 = this.pool[(tail + n)     % TRACK_MAX_POINTS]!;

      if ((p0.x < viewMinX && p1.x < viewMinX) || (p0.x > viewMaxX && p1.x > viewMaxX) ||
          (p0.y < viewMinY && p1.y < viewMinY) || (p0.y > viewMaxY && p1.y > viewMaxY)) continue;

      const age = Math.max(p0.age, p1.age);
      const alpha = Math.max(0, 1 - age / TRACK_MAX_AGE) * 0.45;
      if (alpha < 0.005) continue;

      const cos0 = Math.cos(p0.angle);
      const sin0 = Math.sin(p0.angle);
      const cos1 = Math.cos(p1.angle);
      const sin1 = Math.sin(p1.angle);

      for (const side of [-1, 1] as const) {
        if (segIdx >= MAX_SEGMENTS) break;

        const wx0 = (p0.x - sin0 * TRACK_LATERAL * side) * TILE_SIZE;
        const wz0 = (p0.y + cos0 * TRACK_LATERAL * side) * TILE_SIZE;
        const wx1 = (p1.x - sin1 * TRACK_LATERAL * side) * TILE_SIZE;
        const wz1 = (p1.y + cos1 * TRACK_LATERAL * side) * TILE_SIZE;

        // Build a thin quad perpendicular to the segment direction.
        const dx = wx1 - wx0;
        const dz = wz1 - wz0;
        const len = Math.hypot(dx, dz);
        if (len < 0.001) continue;
        const perpX = -dz / len;
        const perpZ =  dx / len;
        const px = perpX * halfW;
        const pz = perpZ * halfW;

        const v = segIdx * 4 * 3;
        const c = segIdx * 4 * 4;
        this.positions[v    ] = wx0 + px; this.positions[v + 1] = 0; this.positions[v + 2] = wz0 + pz;
        this.positions[v + 3] = wx1 + px; this.positions[v + 4] = 0; this.positions[v + 5] = wz1 + pz;
        this.positions[v + 6] = wx1 - px; this.positions[v + 7] = 0; this.positions[v + 8] = wz1 - pz;
        this.positions[v + 9] = wx0 - px; this.positions[v +10] = 0; this.positions[v +11] = wz0 - pz;

        const cr = TRACK_COLOR_LINEAR.r;
        const cg = TRACK_COLOR_LINEAR.g;
        const cb = TRACK_COLOR_LINEAR.b;
        for (let i = 0; i < 4; i++) {
          this.colors[c + i * 4    ] = cr;
          this.colors[c + i * 4 + 1] = cg;
          this.colors[c + i * 4 + 2] = cb;
          this.colors[c + i * 4 + 3] = alpha;
        }
        segIdx++;
      }
    }

    this.posAttr.needsUpdate = true;
    this.colAttr.needsUpdate = true;
    this.geometry.setDrawRange(0, segIdx * 6);
  }

  reset(): void {
    this.head = 0;
    this.count = 0;
    this.lastX = -999;
    this.lastY = -999;
    this.geometry.setDrawRange(0, 0);
  }

  dispose(): void {
    this.group.remove(this.mesh);
    this.material.dispose();
    this.geometry.dispose();
  }
}
